/**
 * Import der drei Quellformate (Spec 8.1).
 *
 * Alle Dateien auf einmal: im Alltag fallen sie zusammen an, und die Quelle
 * steht in der Datei selbst — sie von Hand zu wählen war eine Fehlerquelle ohne
 * Nutzen. Gelesen wird zuerst, geschrieben erst auf Knopfdruck.
 *
 * Ein gelesener Stapel bleibt am Server liegen, bis er übernommen oder
 * verworfen wird. Er lässt sich deshalb auch später noch aus dem Verlauf
 * übernehmen — vorher ging eine Datei verloren, sobald man die Maske verliess.
 */
import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type ImportBatchDto, type WorkflowDto } from "../lib/api";
import { dateTime } from "../lib/format";
import { NewEmployees } from "../components/NewEmployees";
import { BetroffeneTage, ImportAenderungen } from "../components/ImportAenderungen";
import { ImportRuecknahme } from "../components/ImportRuecknahme";
import { offen, type Aenderung, type Entscheidung, type Pruefung } from "../lib/importPruefung";
import { shortDate } from "../lib/format";
import {
  Amount,
  Empty,
  ErrorBox,
  Info,
  PageHeader,
  Section,
  Spinner,
  importStatusLabel,
  sourceLabel,
} from "../components/ui";

interface Gelesen {
  filename: string;
  ok: true;
  batchId: string;
  sourceType: string;
  erkanntAls: string;
  rowCount: number;
  parsedCount: number;
  skippedCount: number;
  skipReasons: { grund: string; anzahl: number }[];
  periodFrom: string | null;
  periodTo: string | null;
  warnings: { code: string; message: string }[];
  overlappingBatches: { id: string; filename: string | null }[];
  duplicateOfBatchId: string | null;
}

interface Abgelehnt {
  filename: string;
  ok: false;
  error: string;
  message: string;
}

type Datei = Gelesen | Abgelehnt;

interface CommitErgebnis {
  batchId: string;
  ok: boolean;
  filename?: string | null;
  sourceType?: string;
  imported?: number;
  skipped?: number;
  error?: string;
  message?: string;
  employees?: NeuerName[];
  korrigiert?: number;
  tageNeuFreizugeben?: string[];
  tageAusgezahlt?: string[];
  aenderungen?: Aenderung[];
  betroffeneTage?: Pruefung["betroffeneTage"];
}

type Entscheidungen = Record<string, Record<string, Entscheidung>>;

/** Personalnummer mit Trinkgeld-Stunden, die noch nicht unter Mitarbeitende angelegt ist. */
interface NeuerName {
  personalNr: number;
  firstName?: string;
  lastName?: string;
  workAreas: string[];
  suggestedTimeClockId: number | null;
}

export function Import() {
  const [dateien, setDateien] = useState<Datei[]>([]);
  const [ergebnis, setErgebnis] = useState<CommitErgebnis[] | null>(null);
  const [kassenTage, setKassenTage] = useState<{ neuFreizugeben: string[]; ausgezahlt: string[] } | null>(null);
  // Je Stapel: welcher Stand gilt für eine abweichende Stundenzeile.
  const [entscheidungen, setEntscheidungen] = useState<Entscheidungen>({});
  const [ruecknahme, setRuecknahme] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const batches = useQuery({
    queryKey: ["imports"],
    queryFn: () => api.get<{ batches: ImportBatchDto[] }>("/imports"),
  });
  const workflow = useQuery({
    queryKey: ["workflow"],
    queryFn: () => api.get<WorkflowDto>("/workflow"),
  });

  const lesen = useMutation({
    mutationFn: async (files: FileList) => {
      const form = new FormData();
      for (const f of Array.from(files)) form.append("file", f);
      return api.upload<{ dateien: Datei[] }>("/imports", form);
    },
    onSuccess: async (data) => {
      setDateien(data.dateien);
      setErgebnis(null);
      setEntscheidungen({});
      await queryClient.invalidateQueries({ queryKey: ["imports"] });
    },
  });

  const uebernehmen = useMutation({
    mutationFn: (batchIds: string[]) =>
      api.post<{ dateien: CommitErgebnis[]; kassenTage?: { neuFreizugeben: string[]; ausgezahlt: string[] } }>(
        "/imports/commit",
        { batchIds, entscheidungen },
      ),
    onSuccess: async (data) => {
      setErgebnis(data.dateien);
      setKassenTage(data.kassenTage ?? null);
      setDateien([]);
      await queryClient.invalidateQueries();
    },
  });

  const verwerfen = useMutation({
    mutationFn: (batchId: string) => api.del(`/imports/${batchId}`),
    onSuccess: async () => {
      setDateien([]);
      await queryClient.invalidateQueries();
    },
  });

  const lesbar = dateien.filter((d): d is Gelesen => d.ok);
  // Was jede gelesene Datei am Bestand ändert — vor dem Übernehmen.
  const pruefungen = useQueries({
    queries: lesbar.map((d) => ({
      queryKey: ["import-pruefung", d.batchId],
      queryFn: () => api.get<Pruefung>(`/imports/${d.batchId}/pruefung`),
    })),
  });
  const pruefungVon = (batchId: string): Pruefung | undefined =>
    pruefungen[lesbar.findIndex((d) => d.batchId === batchId)]?.data;
  const nochOffen = lesbar.reduce(
    (n, d) => n + offen(pruefungVon(d.batchId)?.aenderungen ?? [], entscheidungen[d.batchId] ?? {}),
    0,
  );
  const setzeEntscheidungen = (batchId: string) => (next: Record<string, Entscheidung>) =>
    setEntscheidungen((prev) => ({ ...prev, [batchId]: { ...(prev[batchId] ?? {}), ...next } }));
  const offeneStapel = (batches.data?.batches ?? []).filter((b) => b.status === "PARSED");

  return (
    <>
      <PageHeader label="Import" title="Dateien einlesen">
        Alle drei Dateien zusammen ablegen. Die Software erkennt selbst, welche welche ist.
        Zuerst wird gelesen und geprüft, dann übernommen. Bis dahin bleiben die
        Produktivdaten unberührt.
      </PageHeader>

      {workflow.data?.periode ? (
        <Section label="Stand" title={`Quellen für die ${workflow.data.periode.label}`}>
          <div className="card p-5">
            <p className="mb-2 text-xs text-muted">
              {workflow.data.periode.from.split("-").reverse().join(".")} bis{" "}
              {workflow.data.periode.to.split("-").reverse().join(".")}
              {workflow.data.stand === "neu" ? " · neue Abrechnung, die Hälfte davor ist abgeschlossen" : ""}
            </p>
            <p className="mb-4 text-sm">
              {workflow.data.quellen.every((q) => q.vorhanden) ? (
                <>
                  <strong className="text-ok">Alle drei Quellen liegen vor.</strong> Das heißt nur,
                  dass von jeder Art etwas da ist, nicht, dass jeder Abend abgedeckt ist. Ob
                  einer fehlt, zeigt sich an den Betriebstagen.
                </>
              ) : (
                <>
                  <strong className="text-warn">
                    {workflow.data.quellen.filter((q) => q.vorhanden).length} von{" "}
                    {workflow.data.quellen.length} Quellen vorhanden.
                  </strong>{" "}
                  Ohne alle drei bleibt die Abrechnung unvollständig: die Kassen liefern das
                  Trinkgeld, die Stunden bestimmen die Verteilung.
                </>
              )}
            </p>
            <ul className="space-y-2">
              {workflow.data.quellen.map((q) => (
                <li key={q.typ} className="flex flex-wrap items-baseline gap-2 text-sm">
                  <span className={q.vorhanden ? "chip-ok" : "chip-warn"}>
                    {q.vorhanden ? "liegt vor" : "fehlt"}
                  </span>
                  <span>{sourceLabel(q.typ)}</span>
                  {q.dateiname ? (
                    <span className="truncate text-xs text-muted">{q.dateiname}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </Section>
      ) : null}

      {offeneStapel.length > 0 && dateien.length === 0 ? (
        <div className="mb-6 rounded border border-warn/30 bg-warn/5 px-4 py-3 text-sm">
          <strong className="text-warn">
            {offeneStapel.length === 1
              ? "Eine Datei ist gelesen, aber noch nicht übernommen."
              : `${offeneStapel.length} Dateien sind gelesen, aber noch nicht übernommen.`}
          </strong>{" "}
          Solange das so ist, fehlen ihre Daten in der Abrechnung.
          <div className="mt-2 flex flex-wrap gap-2">
            <button
              className="btn-recommend"
              onClick={() => uebernehmen.mutate(offeneStapel.map((b) => b.id))}
              disabled={uebernehmen.isPending}
            >
              {uebernehmen.isPending ? "Übernimmt" : "Jetzt übernehmen"}
            </button>
            {offeneStapel.map((b) => (
              <button
                key={b.id}
                className="btn-ghost"
                onClick={() => verwerfen.mutate(b.id)}
                title="Diese gelesene Datei verwerfen. Die Produktivdaten bleiben unberührt."
              >
                {b.filename ?? "ohne Namen"} verwerfen
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <Section label="Schritt 1" title="Dateien wählen">
        <div className="card p-5">
          <input
            type="file"
            multiple
            accept=".csv,.xlsx,.xlsm"
            onChange={(e) => {
              const files = e.target.files;
              setErgebnis(null);
              if (files && files.length > 0) lesen.mutate(files);
            }}
            className="field"
          />
          <p className="mt-2 text-xs text-muted">
            Mehrere gleichzeitig möglich. Erkannt wird am Inhalt. Auf den Dateinamen
            kommt es nicht an, die Exporte heißen jeden Monat anders.
          </p>
          {lesen.isPending ? (
            <p className="mt-3 text-sm text-muted">Liest …</p>
          ) : null}
          {lesen.error ? (
            <div className="mt-4">
              <ErrorBox error={lesen.error} />
            </div>
          ) : null}
        </div>
      </Section>

      {dateien.length > 0 ? (
        <Section label="Schritt 2" title="Vorschau">
          <div className="space-y-4">
            {dateien.map((d, i) => (
              <DateiKarte
                key={`${d.filename}-${i}`}
                datei={d}
                pruefung={d.ok ? pruefungVon(d.batchId) : undefined}
                entscheidungen={d.ok ? (entscheidungen[d.batchId] ?? {}) : {}}
                onEntscheidung={d.ok ? setzeEntscheidungen(d.batchId) : () => {}}
              />
            ))}
          </div>

          {lesbar.length > 0 ? (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                className="btn-recommend"
                onClick={() => uebernehmen.mutate(lesbar.map((d) => d.batchId))}
                disabled={uebernehmen.isPending || nochOffen > 0}
                title={nochOffen > 0 ? "Erst bei jeder Abweichung entscheiden, welcher Stand gilt" : undefined}
              >
                {uebernehmen.isPending
                  ? "Übernimmt"
                  : lesbar.length === 1
                    ? "Übernehmen"
                    : `Alle ${lesbar.length} übernehmen`}
              </button>
              <button
                className="btn-ghost"
                onClick={() => {
                  for (const d of lesbar) verwerfen.mutate(d.batchId);
                }}
              >
                Verwerfen
              </button>
              {nochOffen > 0 ? (
                <span className="text-xs text-warn">
                  Noch {nochOffen} {nochOffen === 1 ? "Abweichung" : "Abweichungen"} zu entscheiden.
                </span>
              ) : null}
            </div>
          ) : null}

          {uebernehmen.error ? (
            <div className="mt-4">
              <ErrorBox error={uebernehmen.error} />
            </div>
          ) : null}
        </Section>
      ) : null}

      {ergebnis ? (
        <Section label="Übernommen" title="Ergebnis">
          <div className="card p-5">
            <ul className="space-y-3">
              {ergebnis.map((r) => (
                <li key={r.batchId} className="text-sm">
                  {r.ok ? (
                    <>
                      <span className="chip-ok mr-2">übernommen</span>
                      {r.filename} — {r.imported} Zeilen
                      {r.korrigiert ? `, ${r.korrigiert} korrigiert` : ""}
                      {r.skipped ? `, ${r.skipped} übersprungen` : ""}
                      <TageListe
                        tage={r.tageNeuFreizugeben ?? []}
                        text="Wieder offen, bitte neu freigeben:"
                      />
                      <TageListe
                        tage={r.tageAusgezahlt ?? []}
                        text="Neue Zeilen an ausgezahlten Tagen, dort ändert sich nichts mehr:"
                      />
                    </>
                  ) : (
                    <>
                      <span className="chip-alert mr-2">blockiert</span>
                      {r.error === "aenderungen_offen" && r.aenderungen ? (
                        <div className="mt-2">
                          <p>{r.message}</p>
                          <BetroffeneTage tage={r.betroffeneTage ?? []} />
                          <ImportAenderungen
                            aenderungen={r.aenderungen}
                            entscheidungen={entscheidungen[r.batchId] ?? {}}
                            onChange={setzeEntscheidungen(r.batchId)}
                          />
                          <button
                            className="btn-recommend mt-3"
                            disabled={
                              uebernehmen.isPending ||
                              offen(r.aenderungen, entscheidungen[r.batchId] ?? {}) > 0
                            }
                            onClick={() => uebernehmen.mutate([r.batchId])}
                          >
                            {uebernehmen.isPending ? "Übernimmt" : "Mit diesen Entscheidungen übernehmen"}
                          </button>
                        </div>
                      ) : r.error === "unknown_employees" && r.employees && r.employees.length > 0 ? (
                        <NeueNamen
                          employees={r.employees}
                          pending={uebernehmen.isPending}
                          onCreated={() => uebernehmen.mutate([r.batchId])}
                        />
                      ) : (
                        r.message
                      )}
                    </>
                  )}
                </li>
              ))}
            </ul>
            {kassenTage ? (
              <div className="mt-3 text-sm">
                <TageListe tage={kassenTage.neuFreizugeben} text="Neue Kassendaten, Tag wieder offen, bitte neu freigeben:" />
                <TageListe tage={kassenTage.ausgezahlt} text="Neue Kassendaten an ausgezahlten Tagen, dort ändert sich nichts mehr:" />
              </div>
            ) : null}
          </div>
        </Section>
      ) : null}

      <Section label="Verlauf" title="Frühere Importe">
        {batches.isLoading ? (
          <Spinner />
        ) : (batches.data?.batches.length ?? 0) === 0 ? (
          <Empty>Noch nichts importiert.</Empty>
        ) : (
          <div className="card overflow-hidden">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Datei</th>
                  <th className="w-40">Quelle</th>
                  <th className="w-24 text-right">Zeilen</th>
                  <th className="w-24 text-right">übernommen</th>
                  <th className="w-36">Status</th>
                  <th className="w-44">Zeitpunkt</th>
                  <th className="w-32 text-right" />
                </tr>
              </thead>
              <tbody>
                {batches.data?.batches.map((b) => {
                  const offen = b.status === "PARSED";
                  // Altbestand kommt nicht aus einem Import-Upload; zurückgesetzt
                  // wird er über „Zeitraum zurücksetzen".
                  const ruecknehmbar = b.status === "COMMITTED" && b.sourceType !== "LEGACY";
                  return (
                    <Fragment key={b.id}>
                    <tr className={offen ? "bg-warn/5" : ""}>
                      <td className="max-w-sm truncate">{b.filename ?? "–"}</td>
                      <td className="text-muted">{sourceLabel(b.sourceType)}</td>
                      <td>
                        <Amount value={String(b.rowCount)} muted />
                      </td>
                      <td
                        title={
                          (b.skipSummary ?? []).length > 0
                            ? (b.skipSummary ?? [])
                                .map((r) => `${r.anzahl} × ${r.grund}`)
                                .join("\n")
                            : undefined
                        }
                      >
                        {/* Ein gelesener Stapel hat nichts übersprungen — er ist
                            nur noch nicht übernommen. Eine 0 mit „nicht übernommen"
                            las sich, als sei die Datei abgelehnt worden. */}
                        {offen ? (
                          <span className="text-xs text-muted">–</span>
                        ) : (
                          <>
                            <Amount value={String(b.importedCount)} />
                            {(b.skipSummary ?? []).length > 0 ? (
                              <div className="text-[10px] text-muted">
                                {b.rowCount - b.importedCount} nicht übernommen
                              </div>
                            ) : null}
                          </>
                        )}
                      </td>
                      <td className="text-xs">
                        {offen ? (
                          <button
                            className="btn-outline"
                            onClick={() => uebernehmen.mutate([b.id])}
                            disabled={uebernehmen.isPending}
                            title="Diese gelesene Datei jetzt übernehmen"
                          >
                            Übernehmen
                          </button>
                        ) : (
                          <span className="text-muted">{importStatusLabel(b.status)}</span>
                        )}
                      </td>
                      <td className="text-muted">{dateTime(b.committedAt ?? b.createdAt)}</td>
                      <td className="text-right">
                        {ruecknehmbar ? (
                          <button
                            className="btn-ghost"
                            onClick={() => setRuecknahme(ruecknahme === b.id ? null : b.id)}
                            title="Diesen Import samt seiner Daten zurücknehmen, mit Vorschau"
                          >
                            Zurücknehmen
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {ruecknahme === b.id ? (
                      <tr>
                        <td colSpan={7} className="bg-surface">
                          <ImportRuecknahme batchId={b.id} onDone={() => setRuecknahme(null)} />
                        </td>
                      </tr>
                    ) : null}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </>
  );
}

function DateiKarte({
  datei,
  pruefung,
  entscheidungen,
  onEntscheidung,
}: {
  datei: Datei;
  pruefung: Pruefung | undefined;
  entscheidungen: Record<string, Entscheidung>;
  onEntscheidung: (next: Record<string, Entscheidung>) => void;
}) {
  if (!datei.ok) {
    return (
      <div className="card border-alert/30 p-5">
        <div className="mb-1 flex flex-wrap items-baseline gap-2">
          {/* Lesbar, aber leer an Verwertbarem: etwa ein Stundenexport ohne
              eine Zeile Arbeitszeit. „nicht lesbar" schickte einen auf die
              falsche Fährte. */}
          <span className="chip-alert">
            {datei.error === "nichts_zu_uebernehmen" ? "nichts zu übernehmen" : "nicht lesbar"}
          </span>
          <strong className="text-sm">{datei.filename}</strong>
        </div>
        <p className="text-sm text-muted">{datei.message}</p>
      </div>
    );
  }

  return (
    <div className="card p-5">
      <div className="mb-3 flex flex-wrap items-baseline gap-2">
        <span className="chip-ok">{sourceLabel(datei.sourceType)}</span>
        <strong className="text-sm">{datei.filename}</strong>
        <span className="text-xs text-muted">erkannt an: {datei.erkanntAls}</span>
      </div>

      <div className="mb-3 flex flex-wrap gap-6">
        <Kennzahl label="Zeilen gelesen" wert={datei.rowCount} />
        <Kennzahl label="übernehmbar" wert={datei.parsedCount} />
        <Kennzahl
          label="übersprungen"
          wert={datei.skippedCount}
          info="Zeilen, die in der Datei stehen, aber nichts zur Abrechnung beitragen. Der Grund steht darunter."
          gedaempft
        />
        <div>
          <div className="label mb-1">Zeitraum</div>
          <div className="font-mono text-sm">
            {datei.periodFrom ?? "–"} bis {datei.periodTo ?? "–"}
          </div>
        </div>
      </div>

      {datei.skipReasons.length > 0 ? (
        <ul className="mb-3 space-y-1 border-l-2 border-line pl-3 text-xs text-muted">
          {datei.skipReasons.map((r) => (
            <li key={r.grund}>
              <span className="font-mono tabular-nums">{r.anzahl}</span> × {r.grund}
            </li>
          ))}
        </ul>
      ) : null}

      {datei.duplicateOfBatchId ? (
        <p className="mb-2 text-xs text-warn">
          Diese Datei wurde schon einmal übernommen. Erneut übernehmen ist erlaubt; bereits
          vorhandene Zeilen werden übersprungen.
        </p>
      ) : null}

      {datei.overlappingBatches.length > 0 ? (
        <p className="mb-2 text-xs text-muted">
          Der Zeitraum überschneidet sich mit {datei.overlappingBatches.length} früheren
          Import(en). Bei Teilimporten ist das normal.
        </p>
      ) : null}

      {pruefung && datei.sourceType === "ROSTER_HOURS" ? (
        <p className="mb-2 text-xs text-muted">
          Gegen den Bestand: {pruefung.neu} neu, {pruefung.gleich} schon genau so vorhanden
          {pruefung.aenderungen.length > 0 ? `, ${pruefung.aenderungen.length} abweichend` : ""}.
        </p>
      ) : null}
      {pruefung && pruefung.kassenAbweichungen > 0 ? (
        <p className="mb-2 text-xs text-warn">
          {pruefung.kassenAbweichungen} Belege gibt es schon mit anderem Betrag. Der bisherige
          bleibt. Nach einem Kassenabschluss ändert sich ein Betrag normalerweise nicht; das
          deutet auf einen fehlerhaften Export hin.
        </p>
      ) : null}
      {pruefung ? <BetroffeneTage tage={pruefung.betroffeneTage} /> : null}
      {pruefung ? (
        <ImportAenderungen
          aenderungen={pruefung.aenderungen}
          entscheidungen={entscheidungen}
          onChange={onEntscheidung}
        />
      ) : null}

      {datei.warnings.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-xs text-muted">
            {datei.warnings.length} Hinweise beim Lesen
          </summary>
          <ul className="mt-2 space-y-1 text-xs text-muted">
            {datei.warnings.slice(0, 20).map((w, i) => (
              <li key={i}>{w.message}</li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function Kennzahl({
  label,
  wert,
  info,
  gedaempft,
}: {
  label: string;
  wert: number;
  info?: string;
  gedaempft?: boolean;
}) {
  return (
    <div>
      <div className="label mb-1">
        {label}
        {info ? <Info text={info} /> : null}
      </div>
      <div
        className={`font-mono text-lg tabular-nums ${gedaempft ? "text-muted" : ""}`}
      >
        {wert}
      </div>
    </div>
  );
}

/**
 * Neue Namen aus dem Stundenimport. Früher stand hier nur eine Liste von
 * Personalnummern, und anlegen ließ sich niemand. Jetzt steht der Name aus der
 * Datei schon drin, die Stempeluhr-ID ist vorgeschlagen, und nach dem Anlegen
 * wird der Import gleich noch einmal übernommen. Die IBAN ist freiwillig — wer
 * sie noch nicht hat, findet die Person danach unter Stammdaten → Mitarbeitende.
 */
function NeueNamen({
  employees,
  pending,
  onCreated,
}: {
  employees: NeuerName[];
  pending: boolean;
  onCreated: () => void;
}) {
  return (
    <div className="mt-2">
      <p className="mb-3">
        {employees.length === 1
          ? "In den Stunden steht ein neuer Name mit Trinkgeld-Stunden."
          : `In den Stunden stehen ${employees.length} neue Namen mit Trinkgeld-Stunden.`}{" "}
        Erst anlegen, dann geht der Import weiter. Die IBAN gleich mit eintragen, sonst wartet ihre
        Auszahlung darauf.
      </p>
      <ul className="mb-3 text-xs text-muted">
        {employees.map((e) => (
          <li key={e.personalNr}>
            PersNr {e.personalNr}: {`${e.firstName ?? ""} ${e.lastName ?? ""}`.trim() || "ohne Namen"} ·{" "}
            {e.workAreas.join(", ")}
          </li>
        ))}
      </ul>
      <NewEmployees
        fixedPersonalNr
        initial={employees.map((e) => ({
          personalNr: String(e.personalNr),
          firstName: e.firstName ?? "",
          lastName: e.lastName ?? "",
          timeClockId: e.suggestedTimeClockId ? String(e.suggestedTimeClockId) : "",
          iban: "",
        }))}
        submitLabel={pending ? "Übernimmt …" : "Anlegen und Import übernehmen"}
        onCreated={onCreated}
      />
    </div>
  );
}

/** Betroffene Tage als Links, damit man direkt hinkommt. */
function TageListe({ tage, text }: { tage: string[]; text: string }) {
  if (tage.length === 0) return null;
  return (
    <div className="mt-1 text-xs text-warn">
      {text}{" "}
      {tage.map((d, i) => (
        <span key={d}>
          {i > 0 ? ", " : ""}
          <Link to={`/betriebstage/${d}`} className="underline">
            {shortDate(d)}
          </Link>
        </span>
      ))}
    </div>
  );
}
